import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Tooltip, Circle, Polygon, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Position } from '../../types';
import { getGeofences, Geofence, CircleGeometry, PolygonGeometry } from '../../services/api';
import {
  buildArrowData, detectStopEvents, detectParkingEvents,
  makeStopIcon, makeParkingIcon, fmtLocalTime,
} from '../../utils/routeAnnotations';
import { parseUtc } from '../../utils/dataAge';
import 'leaflet/dist/leaflet.css';
import './LiveMap.css';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const LAYERS = {
  osm:       { name: '🗺️ Mapa',     url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', maxZoom: 19, attr: '© OpenStreetMap' },
  carto:     { name: '✏️ Czysty',   url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', maxZoom: 19, attr: '© Carto' },
  satellite: { name: '🛰️ Satelita', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', maxZoom: 23, attr: '© Esri' },
};
type LayerKey = keyof typeof LAYERS;

// Singleton icons — stable references across renders
const stopIcon    = makeStopIcon();
const parkingIcon = makeParkingIcon();

interface LiveMapProps {
  positions: Position[];
  allPositions?: Position[];
  currentPosition?: Position;
  deviceInfo?: { name: string; registration?: string };
  center?: [number, number];
  zoom?: number;
  onMapClick?: () => void;
  /** When set, triggers flyTo on this [lat, lng]. Change the value to re-trigger. */
  flyToTarget?: { lat: number; lng: number; ts: number } | null;
}

const MapUpdater: React.FC<{ center: [number, number] }> = ({ center }) => {
  const map = useMap();
  useEffect(() => { map.setView(center, map.getZoom()); }, [center, map]);
  return null;
};

/** Reacts to flyToTarget changes and performs animated flyTo with smart zoom. */
const MapFly: React.FC<{ target: LiveMapProps['flyToTarget'] }> = ({ target }) => {
  const map = useMap();
  useEffect(() => {
    if (!target) return;
    const targetZoom = map.getZoom() < 13 ? 15 : map.getZoom();
    map.flyTo([target.lat, target.lng], targetZoom, { duration: 0.8 });
  }, [target, map]);
  return null;
};

const filterPositions = (pts: Position[]): Position[] => {
  const out: Position[] = [];
  for (const p of pts) {
    let ignition: boolean | undefined;
    try {
      const attrs = p.attributes ? JSON.parse(p.attributes) : {};
      const hdop    = attrs.hdop    as number | undefined;
      const sat     = attrs.sat     as number | undefined;
      const fixType = attrs.fixType as number | undefined;
      ignition      = attrs.ignition as boolean | undefined;
      if (fixType !== undefined && fixType < 3) continue;
      const satMin = hdop === undefined ? 4 : 6;
      if ((hdop !== undefined && hdop > 2.0) || (sat !== undefined && sat < satMin)) continue;
    } catch { /* no attrs — pass through */ }

    if (out.length === 0) { out.push(p); continue; }
    const prev = out[out.length - 1];
    const dLat = (p.latitude  - prev.latitude)  * 111000;
    const dLng = (p.longitude - prev.longitude) * 111000 * Math.cos(p.latitude * Math.PI / 180);
    const dist = Math.sqrt(dLat * dLat + dLng * dLng);
    const minDist = (ignition === false && (p.speed ?? 0) === 0) ? 15 : 5;
    if (dist > minDist) out.push(p);
  }
  return out;
};

const LiveMap: React.FC<LiveMapProps> = ({
  positions, allPositions, currentPosition, deviceInfo, center, zoom = 13, onMapClick, flyToTarget,
}) => {
  const [mapCenter, setMapCenter] = useState<[number, number]>(center || [51.2590266, 22.4958933]);
  const [activeLayer, setActiveLayer] = useState<LayerKey>('osm');
  const [geofences, setGeofences]     = useState<Geofence[]>([]);
  const [showZones,   setShowZones]   = useState(true);
  const [showStop,    setShowStop]    = useState(false);
  const [showParking, setShowParking] = useState(false);

  useEffect(() => {
    getGeofences()
      .then(gs => setGeofences(gs.filter(g => g.active)))
      .catch(err => console.warn('[LiveMap] failed to load geofences', err));
  }, []);

  useEffect(() => {
    if (positions.length > 0) {
      const latest = positions[0];
      if ((latest.speed ?? 0) > 2) {
        setMapCenter([latest.latitude, latest.longitude]);
      } else if (mapCenter[0] === 51.2590266 && mapCenter[1] === 22.4958933) {
        setMapCenter([latest.latitude, latest.longitude]);
      }
    }
  }, [positions]);

  // history-date endpoint returns oldest→newest (ORDER BY fixtime ASC) — already chronological.
  // Do NOT reverse. filteredPositions reverses separately for display/arrows (newest-first),
  // which is why buildArrowData bearing needs +180° to point forward.
  const chronologicalPositions = useMemo<Position[]>(
    () => positions,
    [positions],
  );

  // Filtered positions newest-first — used for route display and arrows.
  const filteredPositions = useMemo<Position[]>(() => {
    if (positions.length < 2) return [];
    return filterPositions([...positions].reverse());
  }, [positions]);

  const effectiveRoute = useMemo<[number, number][]>(
    () => filteredPositions.map(p => [p.latitude, p.longitude] as [number, number]),
    [filteredPositions],
  );

  const arrowData      = useMemo(() => buildArrowData(filteredPositions, 5),          [filteredPositions]);
  const stopEvents     = useMemo(() => detectStopEvents(chronologicalPositions),      [chronologicalPositions]);
  const parkingEvents  = useMemo(() => detectParkingEvents(chronologicalPositions),   [chronologicalPositions]);

  return (
    <div className="map-wrapper" onClick={onMapClick}>
      <div className="layer-switcher" onClick={e => e.stopPropagation()}>

        {/* Layer buttons */}
        {(Object.keys(LAYERS) as LayerKey[]).map(k => (
          <button
            key={k}
            className={`layer-btn${activeLayer === k ? ' active' : ''}`}
            onClick={() => setActiveLayer(k)}
          >
            {LAYERS[k].name}
          </button>
        ))}

        {/* Zones toggle */}
        {geofences.length > 0 && (
          <button
            className={`layer-btn${showZones ? ' active' : ''}`}
            onClick={() => setShowZones(v => !v)}
            title={`${geofences.length} aktywnych stref`}
          >
            🚧 Strefy
          </button>
        )}

        {/* STOP toggle — road sign B-20 */}
        <button
          className="layer-btn"
          onClick={() => setShowStop(v => !v)}
          title="Postoje z pracującym silnikiem"
          style={{ opacity: showStop ? 1 : 0.4, padding: '2px 5px', lineHeight: 0 }}
        >
          <svg width="22" height="22" viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg">
            <polygon
              points="8,1 14,1 21,8 21,14 14,21 8,21 1,14 1,8"
              fill="#dc2626" stroke="white" strokeWidth="1.5" strokeLinejoin="round"
            />
            <text x="11" y="13.5" textAnchor="middle" fill="white"
              fontSize="5" fontWeight="bold" fontFamily="Arial,sans-serif">
              STOP
            </text>
          </svg>
        </button>

        {/* Parking toggle — road sign D-18 */}
        <button
          className="layer-btn"
          onClick={() => setShowParking(v => !v)}
          title="Postoje (wyłączony silnik)"
          style={{ opacity: showParking ? 1 : 0.4, padding: '2px 5px', lineHeight: 0 }}
        >
          <svg width="22" height="22" viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg">
            <rect x="1" y="1" width="20" height="20" rx="3"
              fill="#2b6cc4" stroke="white" strokeWidth="1.5" />
            <text x="11" y="16" textAnchor="middle" fill="white"
              fontSize="13" fontWeight="bold" fontFamily="Arial,sans-serif">
              P
            </text>
          </svg>
        </button>
      </div>

      <MapContainer center={mapCenter} zoom={zoom} maxZoom={23} style={{ height: '100%', width: '100%' }}>
        <TileLayer
          key={activeLayer}
          attribution={LAYERS[activeLayer].attr}
          url={LAYERS[activeLayer].url}
          maxZoom={LAYERS[activeLayer].maxZoom}
        />
        <MapUpdater center={mapCenter} />
        <MapFly target={flyToTarget} />

        {/* Geofence zones */}
        {showZones && geofences.map(g => {
          const opts = { color: g.color, fillOpacity: 0.15, weight: 2, interactive: false } as const;
          return g.geometry_type === 'circle' ? (
            <Circle
              key={`zone-${g.id}`}
              center={(g.geometry as CircleGeometry).center}
              radius={(g.geometry as CircleGeometry).radius}
              pathOptions={opts}
            />
          ) : (
            <Polygon
              key={`zone-${g.id}`}
              positions={(g.geometry as PolygonGeometry).points}
              pathOptions={opts}
            />
          );
        })}

        {/* Route polyline */}
        {effectiveRoute.length > 1 && (
          <Polyline positions={effectiveRoute} color="#4a6fa5" weight={4} opacity={0.85} />
        )}

        {/* Direction arrows — always visible, every 5 filtered positions */}
        {arrowData.map((arrow, i) => (
          <Marker key={`arr-${i}`} position={arrow.position} icon={arrow.icon}>
            <Tooltip direction="top" offset={[0, -10]}>
              <div style={{ fontSize: 12, lineHeight: '1.7', whiteSpace: 'nowrap' }}>
                <b>Prędkość:</b> {arrow.speedKmh} km/h<br />
                {arrow.rpm != null && (
                  <><b>Obroty:</b> {arrow.rpm} rpm<br /></>
                )}
                <b>Czas:</b> {fmtLocalTime(arrow.fixtime)}
              </div>
            </Tooltip>
          </Marker>
        ))}

        {/* STOP events — ignition=true, speed≈0, >60 s */}
        {showStop && stopEvents.map((ev, i) => (
          <Marker key={`stop-${i}`} position={ev.position} icon={stopIcon}>
            <Tooltip direction="top" offset={[0, -16]}>
              <div style={{ fontSize: 12, lineHeight: '1.7', whiteSpace: 'nowrap' }}>
                <b>Postój silnik pracuje</b><br />
                Początek: {fmtLocalTime(ev.startTime)}<br />
                Czas trwania: {ev.durationMin} min
              </div>
            </Tooltip>
          </Marker>
        ))}

        {/* Parking events — ignition=false, >3 min */}
        {showParking && parkingEvents.map((ev, i) => (
          <Marker key={`park-${i}`} position={ev.position} icon={parkingIcon}>
            <Tooltip direction="top" offset={[0, -16]}>
              <div style={{ fontSize: 12, lineHeight: '1.7', whiteSpace: 'nowrap' }}>
                <b>Postój wyłączony silnik</b><br />
                Początek: {fmtLocalTime(ev.startTime)}<br />
                Czas trwania: {ev.durationMin} min
              </div>
            </Tooltip>
          </Marker>
        ))}

        {/* Current vehicle position */}
        {currentPosition && (
          <Marker position={[currentPosition.latitude, currentPosition.longitude]}>
            {deviceInfo && (
              <Tooltip permanent direction="top" offset={[0, -32]} className="vehicle-label">
                <div>{deviceInfo.name}</div>
                {deviceInfo.registration && <div>{deviceInfo.registration}</div>}
              </Tooltip>
            )}
            <Popup>
              <div style={{ minWidth: '150px' }}>
                <strong>Prędkość:</strong> {Math.round(currentPosition.speed_kmh ?? 0)} km/h<br />
                <strong>Czas:</strong> {parseUtc(currentPosition.fixtime).toLocaleString(
                  undefined, { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }
                )}
              </div>
            </Popup>
          </Marker>
        )}

        {/* All devices markers */}
        {allPositions && allPositions.length > 0 && allPositions.map(position => (
          <Marker
            key={`all-${position.id}`}
            position={[position.latitude, position.longitude]}
            title={`Device ${position.deviceid}`}
          >
            <Popup>
              <div style={{ minWidth: '150px' }}>
                <strong>Device ID:</strong> {position.deviceid}<br />
                <strong>Last Update:</strong> {parseUtc(position.fixtime).toLocaleString(
                  undefined, { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }
                )}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default LiveMap;
